import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, TrendingUp, Globe } from 'lucide-react';

export default function AIPreviewPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if popup was dismissed in this session
    const dismissed = sessionStorage.getItem('ai-popup-dismissed');
    if (!dismissed) {
      // Show popup after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('ai-popup-dismissed', 'true');
  };

  const handleExplore = () => {
    handleDismiss();
    navigate('/ai-predictor');
  };

  const handleSignUp = () => {
    handleDismiss();
    navigate('/auth?mode=register');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Popup */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-[#001435] via-[#0a2a5c] to-[#1a4a8c] p-6 pb-8">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            data-testid="ai-popup-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-medium text-white/90">New Feature</span>
          </div>
          
          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            AI Powered Currency & Travel Predictor
          </h2>
          
          {/* Floating Icons */}
          <div className="flex gap-3 mt-4">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 leading-relaxed mb-6">
            Plan smarter before you exchange. Explore currency predictions, compare travel value by country, and see where your Canadian dollar may go further.
          </p>
          
          {/* Features List */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              Currency Predictions
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              Travel Value Finder
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              Smart Insights
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-2 h-2 bg-amber-500 rounded-full" />
              Preferred Rates
            </div>
          </div>
          
          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleExplore}
              className="w-full py-3.5 bg-gradient-to-r from-[#001435] to-[#1a4a8c] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
              data-testid="ai-popup-explore"
            >
              Explore AI Predictor
            </button>
            
            <button
              onClick={handleSignUp}
              className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors"
              data-testid="ai-popup-signup"
            >
              Sign Up & Get Preferred Rates
            </button>
            
            <button
              onClick={handleDismiss}
              className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
              data-testid="ai-popup-later"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
